import React, { useMemo, useRef } from 'react';
import AsyncGenericSelect from '../../../components/ui/AsyncGenericSelect';
import { makeLookup } from '../../attendance-dashboard/config/useAttendanceDashboardApi';
import { getDepartments, getDepartment } from '../../../services/departmentService';
import { getLocations, getLocation } from '../../../services/locationService';
import { getUserGroups, getUserGroup } from '../../../services/userGroupService';
import { getEmployeeStatuses, getEmployeeStatus } from '../../../services/employeeStatusService';
import { getWorkHours, getWorkHour } from '../../../services/workHourService';
import { getEmployees, getEmployee } from '../../../services/employeeService';

// AKM POC §7.5 — PDKS raporlarının ORTAK süzgeç seti (Devam Paneli sözleşmesiyle aynı tekil adlar): personel statüsü,
// personel grubu, departman (birincil pozisyon), lokasyon, vardiya, kişi. Her biri tabloda görünmeyen bir sütundur
// (display 'excluded') ve DataTable'ın süzgeç penceresinde aranabilir seçici olarak çıkar. parseTableParams düz değeri
// `filterOptions.filterKey` adıyla gönderir (utils/Utils.js) — {op,val} biçimi KULLANILMAZ (orada filterKey ölü).
export const TA_FILTER_KEYS = ['employeeStatusId', 'userGroupId', 'departmentId', 'locationId', 'workHourId', 'personId'];

const readList = (res) => {
  const payload = res?.data?.data;
  if (Array.isArray(payload)) return payload;
  return payload?.data ?? [];
};

const personName = (p) => p?.fullName || [p?.firstName, p?.lastName].filter(Boolean).join(' ').trim() || p?.name || '';

const LOOKUPS = {
  employeeStatusId: { labelKey: 'taReports.filters.employeeStatus', ...makeLookup(getEmployeeStatuses, getEmployeeStatus, { ordered: false }) },
  userGroupId: { labelKey: 'taReports.filters.userGroup', ...makeLookup(getUserGroups, getUserGroup) },
  departmentId: { labelKey: 'taReports.filters.department', ...makeLookup(getDepartments, getDepartment) },
  locationId: { labelKey: 'taReports.filters.location', ...makeLookup(getLocations, getLocation) },
  workHourId: { labelKey: 'taReports.filters.workHour', ...makeLookup(getWorkHours, getWorkHour, { ordered: false }) },
  personId: {
    labelKey: 'taReports.filters.person',
    // Depodaki kişi seçici kalıbı: fullName[like] + pageSize 20 (LicensesPage notu). Kimlik = Employee.Id — sunucu
    // kişi süzgecini kimlik ailesiyle (User.Id ya da Employee.Id) eşler.
    onSearch: async (query) => {
      const params = { pageSize: 20, lookup: true };
      if (query) params['fullName[like]'] = query;
      return readList(await getEmployees(params)).map((e) => ({ ...e, name: personName(e) }));
    },
    onGetById: async (id) => {
      const res = await getEmployee(id);
      const e = res?.data?.status === false ? null : res?.data?.data;
      return e ? { ...e, name: personName(e) } : null;
    },
  },
};

/**
 * Süzgeç sütunları. `include` ile alt küme (ör. Geçiş Kayıtları yalnız departman + lokasyon). Seçilen değerin adı bir
 * önbellekte tutulur ki süzgeç rozeti "Departman: Sahne Teknik" diye okunsun (kimlik değil).
 */
export function useTaFilterColumns(t, include = TA_FILTER_KEYS) {
  const names = useRef({});
  const includeKey = (include || []).join(',');

  return useMemo(() => includeKey.split(',').filter((key) => LOOKUPS[key]).map((key) => {
    const lookup = LOOKUPS[key];
    const label = t(lookup.labelKey);
    // Seçilen değerin adı: arama sonuçlarından (elle seçimde AsyncGenericSelect onGetById çağırmaz) ve kimlikle çözümden.
    const onSearch = async (query) => {
      const items = (await lookup.onSearch(query)) || [];
      items.forEach((item) => { names.current[`${key}:${item.id}`] = item.name || personName(item); });
      return items;
    };
    const onGetById = async (id) => {
      const item = await lookup.onGetById(id);
      if (item) names.current[`${key}:${id}`] = item.name || personName(item);
      return item;
    };
    return {
      name: key,
      label,
      options: {
        display: 'excluded',
        viewColumns: false,
        download: false,
        sort: false,
        filter: true,
        filterType: 'custom',
        filterList: [],
        filterOptions: {
          filterKey: key,
          logic: () => false,
          display: (filterList, onChange, index, column) => (
            <AsyncGenericSelect
              compact
              placeholder={label}
              value={filterList[index]?.[0] || null}
              onSearch={onSearch}
              onGetById={onGetById}
              onChange={(id) => {
                filterList[index] = id ? [id] : [];
                onChange(filterList[index], index, column);
              }}
            />
          ),
        },
        customFilterListOptions: {
          render: (v) => {
            const id = Array.isArray(v) ? v[0] : v;
            if (!id) return [];
            return [`${label}: ${names.current[`${key}:${id}`] || '…'}`];
          },
          update: (filterList, filterPos, index) => {
            filterList[index] = [];
            return filterList;
          },
        },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, includeKey]);
}
